import { Check } from "lucide-react";
import Eyebrow from "../components/Eyebrow";
import Breadcrumbs from "../components/Breadcrumbs";
import CTAButton from "../components/CTAButton";
import ProcessSteps from "../components/ProcessSteps";
import FAQAccordion from "../components/FAQAccordion";
import { Reveal, KineticLines } from "../components/Reveal";
import { usePageMeta } from "../lib/seo";
import { SITE, COMPENSATION_DISCLOSURE } from "../lib/site";

const TIMING_FAQS = [
  {
    q: "How quickly can we start?",
    a: "The first conversation can usually happen within a day. CareCraft responds to calls and emails within 24 hours.",
  },
  {
    q: "How long does the process take?",
    a: "Every family is different. Some families take weeks to explore and consider; others need to move within days of a hospital discharge. The pace is yours to set, and CareCraft helps make it considered rather than rushed either way.",
  },
  {
    q: "What if we need to move urgently?",
    a: "CareCraft responds to calls and emails within 24 hours, and sometimes families have only a short time to plan. Contact us and tell us what is happening so we can understand how we may be able to help. Please note that CareCraft is not an emergency service. For a medical emergency, please contact the appropriate emergency services.",
  },
  {
    q: "What happens in the first conversation?",
    a: "We listen. You tell us what's happening, what matters to your family, and what you're concerned about, and we help you understand what options may make sense. There's no obligation. Share what's on your mind, and we'll take it from there.",
  },
];

export default function HowItWorks() {
  usePageMeta({
    title: "How It Works: The CareCraft Way",
    description:
      "Understand, Curate, Explore, Decide, Transition. How CareCraft guides Los Angeles families through senior care decisions, with complimentary guidance and the final decision always remaining with the family.",
    path: "/how-it-works",
  });

  return (
    <>
      <section className="bg-ivory pt-[72px]">
        <div className="mx-auto max-w-site px-4 pb-16 pt-10 sm:px-6 md:pb-20 md:pt-14 lg:px-12">
          <Breadcrumbs trail={[{ label: "How It Works" }]} />
          <div className="mt-10 max-w-3xl">
            <Eyebrow>The CareCraft way</Eyebrow>
            <h1 className="mt-6 font-serif text-4xl font-medium leading-[1.15] tracking-tight text-forest sm:text-5xl lg:text-[3.6rem]">
              <KineticLines
                lines={[<>A thoughtful way to work</>, <>through the next steps together.</>]}
              />
            </h1>
            <Reveal delay={0.4}>
              <p className="mt-7 text-lg leading-[1.7] text-charcoal/80 md:text-xl">
                Five thoughtful steps to help your family understand the options, consider what may
                fit, and decide what feels right for the person you care for. CareCraft stays
                alongside you through the process, while the final decision always remains with you
                and your family.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-warmwhite py-20 md:py-28">
        <div className="mx-auto max-w-site px-4 sm:px-6 lg:px-12">
          <ProcessSteps detailed />
        </div>
      </section>

      <section className="bg-ivory py-20 md:py-28">
        <div className="mx-auto grid max-w-site gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-12">
          <Reveal>
            <div className="h-full rounded-2xl border border-inkline bg-warmwhite p-8 md:p-10">
              <h2 className="font-serif text-2xl font-medium text-forest md:text-[1.7rem]">
                How tours work
              </h2>
              <p className="mt-4 leading-[1.7] text-charcoal/80">
                Once there's a short list worth considering, CareCraft coordinates visits on your
                family's timeline and helps you prepare for them.
              </p>
              <ul className="mt-6 space-y-3">
                {[
                  "Visits arranged around your schedule, including urgent timelines.",
                  "Guidance on what to look for beyond the brochure: the environment, staff interactions, and daily rhythms.",
                  "Questions worth asking, and what the answers can tell you.",
                  "A chance afterward to talk through what you saw, what it means, and what's worth a second look.",
                ].map((q) => (
                  <li key={q} className="flex gap-3 leading-[1.6] text-charcoal/80">
                    <Check className="mt-1 h-4 w-4 shrink-0 text-clay" aria-hidden="true" />
                    {q}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="h-full rounded-2xl border border-inkline bg-warmwhite p-8 md:p-10">
              <h2 className="font-serif text-2xl font-medium text-forest md:text-[1.7rem]">
                What the guidance costs
              </h2>
              <p className="mt-4 font-serif text-xl italic text-forest">
                Our guidance is complimentary to families.
              </p>
              <p className="mt-4 leading-[1.7] text-charcoal/80">{COMPENSATION_DISCLOSURE}</p>
              <ul className="mt-6 space-y-3">
                {["No consultation fee", "No fee for tours", "No placement fee paid by your family"].map(
                  (p) => (
                    <li key={p} className="flex gap-3 font-medium text-forest">
                      <Check className="mt-1 h-4 w-4 shrink-0 text-clay" aria-hidden="true" />
                      {p}
                    </li>
                  )
                )}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="grain bg-forest py-20 text-ivory md:py-24">
        <div className="mx-auto max-w-site px-4 sm:px-6 lg:px-12">
          <Reveal>
            <Eyebrow tone="sage">One thing we want to be clear about</Eyebrow>
            <p className="mt-6 max-w-3xl font-serif text-3xl font-medium leading-snug md:text-4xl">
              The decision remains with the family.
            </p>
            <p className="mt-5 max-w-2xl text-ivory/80">
              CareCraft helps you understand the options, narrows the possibilities, and stays
              alongside you. But the choice of care, and the timing, always belongs to you.
            </p>
            <CTAButton to="/contact" variant="ivory" className="mt-9" testId="how-it-works-final-cta">
              Start With a Conversation
            </CTAButton>
          </Reveal>
        </div>
      </section>

      <section className="bg-warmwhite py-20 md:py-28">
        <div className="mx-auto max-w-site px-4 sm:px-6 lg:px-12">
          <Reveal>
            <Eyebrow>Timing</Eyebrow>
            <h2 className="mt-6 max-w-2xl font-serif text-3xl font-medium tracking-tight text-forest md:text-4xl">
              Questions about timing
            </h2>
          </Reveal>
          <div className="mt-10 max-w-4xl">
            <FAQAccordion items={TIMING_FAQS} />
          </div>
          <Reveal delay={0.1}>
            <p className="mt-6 text-[0.85rem] text-charcoal/55">
              More questions? Find the full list on the{" "}
              <a href="/resources" data-testid="how-it-works-link-faq" className="stitch-underline text-forest">
                Resources &amp; FAQ
              </a>{" "}
              page, or call {SITE.phoneDisplay}.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-ivory py-20 md:py-24">
        <div className="mx-auto max-w-site px-4 sm:px-6 lg:px-12">
          <Reveal>
            <Eyebrow>When families need something more</Eyebrow>
            <h2 className="mt-6 max-w-2xl font-serif text-3xl font-medium tracking-tight text-forest md:text-4xl">
              Care decisions affect more than logistics.
            </h2>
            <p className="mt-5 max-w-2xl text-lg leading-[1.7] text-charcoal/75">
              For those who would find it helpful, CareCraft can also connect families with
              additional personal support.
            </p>
          </Reveal>
          <div className="mt-10 grid gap-6 md:grid-cols-2">
            {[
              {
                title: "Life Coaching",
                body: "A space to work through transition, uncertainty, and the emotions that can accompany changing care needs.",
              },
              {
                title: "Prayer Support",
                body: "For families who would welcome spiritual support during the process.",
              },
            ].map((it, i) => (
              <Reveal key={it.title} delay={0.08 * i}>
                <article className="h-full rounded-2xl border border-inkline bg-warmwhite p-8">
                  <h3 className="font-serif text-2xl font-medium text-forest">{it.title}</h3>
                  <p className="mt-3 leading-[1.7] text-charcoal/80">{it.body}</p>
                </article>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <p className="mt-9 font-serif text-2xl italic text-forest">
              Entirely optional. Always personal.
            </p>
          </Reveal>
        </div>
      </section>
    </>
  );
}
